import React, { ChangeEvent, useContext, useState } from 'react';
import {
  createStyles,
  Divider,
  Paper,
  TextField,
  Theme,
  Tooltip,
  withStyles
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import colors from '../../../../../styles/colors';
import AssignmentWithPenIcon from '../../../common/icons/AssignmentWithPenIcon';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import SchoolIcon from '@material-ui/icons/School';
import ListIcon from '@material-ui/icons/List';
import {
  AssignmentEditState,
  AssignmentEditStatus
} from '../state/assignment-edit.types';
import { ordinalSuffix } from '../../../../../utils/grade';
import { Button } from '../../../common/button/Button';
import { Delete, FileCopy } from '@material-ui/icons';
const useStyles = makeStyles({
  root: {
    padding: '1.2rem',
    border: `1px solid ${colors.assignment.grey}`
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  divider: {
    margin: '1rem 0'
  },
  list: {
    display: 'flex',
    flexDirection: 'column'
  },
  listText: {
    marginLeft: '10px'
  },
  text: {
    color: colors.assignment.lightgrey
  },
  descriptionLabel: {
    display: 'block',
    color: colors.assignment.lightgrey,
    marginLeft: '10px',
    marginBottom: '10px'
  }
});

const CustomTextArea = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderRadius: `4px`
        }
      }
    }
  })
)(TextField);

interface AssignmentMetaDataProps {
  assignment: Partial<AssignmentEditState>;
  handleAssignmentEdit(data: Partial<AssignmentEditState>): void;
  isEditMode: boolean;
  isPremade: boolean;
  isError: boolean;
  handleAssign(): any;
  handleDuplicate(): void;
  handleAssignmentModal(): any;
  handleDeleteAssignment(): void;
  handleSave(): any;
}

const AssignmentMetaData: React.FC<AssignmentMetaDataProps> = (
  props: AssignmentMetaDataProps
) => {
  const classes = useStyles();
  const {
    assignment,
    handleAssignmentEdit,
    handleAssignmentModal,
    isEditMode,
    isPremade,
    handleDuplicate,
    handleSave,
    isError,
    handleAssign,
    handleDeleteAssignment
  } = props;

  const { name, description, grade } = assignment;

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    handleAssignmentEdit({
      [evt.target.name]: evt.target.value
    });
  };

  const gradeString = ordinalSuffix(grade as number);
  let questionCount = 0;
  assignment.data?.forEach(passageOrQuestion => {
    if (passageOrQuestion.questions) {
      questionCount += passageOrQuestion.questions.length;
    } else {
      questionCount += 1;
    }
  });
  return (
    <Paper className={classes.root} elevation={0}>
      <Grid container>
        <Grid item className={classes.header} xs={12}>
          <Typography
            variant={'h6'}
            title={name}
            style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
          >
            {name}
          </Typography>

          <div onClick={handleAssignmentModal}>
            <AssignmentWithPenIcon
              style={{ cursor: 'pointer' }}
              variant={'outlined'}
            />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {!isPremade && isEditMode && (
            <Button
              onClick={handleSave}
              variant={'outline'}
              color={'black'}
              style={{ padding: '.5rem 1rem', margin: '.5rem 1rem' }}
            >
              Save
            </Button>
          )}
          {!isPremade && isEditMode && (
            <Tooltip
              style={{
                fontSize: '1.2rem'
              }}
              title={
                isError
                  ? 'All errors must be fixed before it can be assigned.'
                  : ''
              }
            >
              <div>
                <Button
                  variant={'filled'}
                  disabled={isError}
                  onClick={handleAssign}
                  style={{ padding: '.5rem 1rem' }}
                  textColor={'white'}
                  color={colors.assignment.blue}
                >
                  Assign
                </Button>
              </div>
            </Tooltip>
          )}
        </Grid>
        <Grid
          className={classes.text}
          item
          xs={12}
          style={{ marginTop: '1rem' }}
        >
          <div className={classes.list}>
            {grade && grade > 0 && gradeString && (
              <div>
                <SchoolIcon />
                <span className={classes.listText}>{gradeString} Grade</span>
              </div>
            )}
            <div>
              <ListIcon />
              <span className={classes.listText}>
                {questionCount} {questionCount === 1 ? 'Question' : 'Questions'}
              </span>
            </div>
          </div>
        </Grid>
        <Grid item className={classes.divider} xs={12}>
          <Divider />
        </Grid>
        {isEditMode ? (
          <Grid item xs={12}>
            <label className={classes.descriptionLabel} htmlFor="description">
              Description (Optional)
            </label>
            <CustomTextArea
              fullWidth={true}
              disabled={!isEditMode}
              id="description"
              multiline
              onChange={handleChange}
              rows={4}
              name={'description'}
              value={description || ''}
              variant="outlined"
            />
          </Grid>
        ) : (
          <Grid item xs={12} style={{ marginTop: '10px' }}>
            <span>{description}</span>
          </Grid>
        )}
        <Grid item xs={12} style={{ marginTop: isEditMode ? '15px' : 0 }}>
          <Grid container alignItems={'center'} justify={'center'} spacing={2}>
            {!isEditMode && !isPremade && (
              <Grid item xs={6}>
                <Button
                  variant={'outline'}
                  style={{ width: '100%' }}
                  textColor={'grey'}
                  color={'grey'}
                  onClick={handleDuplicate}
                  icon={FileCopy}
                >
                  Duplicate
                </Button>
              </Grid>
            )}
            <Grid item xs={6}>
              <Button
                variant={'outline'}
                style={{ width: '100%' }}
                textColor={'grey'}
                onClick={handleDeleteAssignment}
                color={'grey'}
                icon={Delete}
              >
                Delete
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AssignmentMetaData;
